/*
  # Accordion

      <div data-hook="accordion"
        data-accordion-id="accordion">
        <button data-hook="accordion.button" type="button">
          <!-- Contents of button, including icon -->
        </button>
        <div data-hook="accordion.outer"
          class="overflow-hidden p-relative"
          data-class-accordion-open="d-block"
          data-class-accordion-closing="d-none"
          data-class-accordion-closed="d-none">
          <div data-hook="accordion.inner" class="p-relative">
            <!-- Contents of accordion -->
          </div>
        </div>
      </div>
  Styles can be controlled either from this script by changing
  where the data-class-accordion-open and
  data-class-accordion-closed attributes on the accordion
  or any descendant. The data-class-accordion-closing
  assists with the limitations of CSS transitions
  created by the display property, which is the useful
  way to hide the accordion.outer element.

  The following data-hook token values apply to this
  controller:

  - accordion - The wrapper for each accordion.
  - accordion.button - The button which opens and closes the
    accordion.
  - accordion.outer - The element which changes size.
  - accordion.inner - The element which contains the expanded content
    and which is measured to determine the expanded size of
    accordion.outer.

    The data-collapsed-height attribute determines the height that
  the accordion animates to when it's collapsed. It is interpreted
  as a CSS value, not a number of pixels. The default is '0'.

  Use with the accordionViewportLimit controller to limit the
  range at which the accordion controller is applicable. This
  is available from the accordion-viewport-limit pattern.

  Use the accordion-quick-close pattern and accordionQuickClose
  controller to close the accordion when the user clicks outside
  of it.

*/

import {
  assignController, setClassState, unsetClassState, listWithAncestors,
  selectAll, selectAlso,
} from '../api/element-manipulation'
import { refreshBgImages } from '../api/controller-manipulation'

assignController('accordion', (element) => {
  const opened = []
  const closed = []
  const outer = element.querySelector('[data-hook~="accordion.outer"]')
  const inner = element.querySelector('[data-hook~="accordion.inner"]')
  const button = element.querySelector('[data-hook~="accordion.button"]')
  const collapsedHeight = element.dataset.collapsedHeight || '0'
  const getOpenHeight = () => {
    if (inner) {
      return inner.clientHeight
    }
    return (Array.from(outer.children).reduce((value, child) => (child.clientHeight || 0) + value, 0))
  }
  if (!button.getAttribute('id') && element.dataset.accordionId) {
    const buttonId = `accordion-${element.dataset.accordionId}-button`
    button.setAttribute('id', buttonId)
    outer.setAttribute('aria-labelledby', buttonId)
  }
  if (!outer.getAttribute('id') && element.dataset.accordionId) {
    const outerId = `accordion-${element.dataset.accordionId}-panel`
    outer.setAttribute('id', outerId)
    button.setAttribute('aria-controls', outerId)
  }
  if (collapsedHeight !== '0') {
    const handleInteraction = () => open()
    selectAll(outer, 'a, button, input').forEach((focusable) => {
      focusable.addEventListener('focus', handleInteraction)
    })
  }
  const close = () => {
    let defaultPrevented = false
    const event = {
      sender: controller,
      preventDefault: () => {
        defaultPrevented = true
      },
    }
    closed.forEach(listener => listener(event))
    if (defaultPrevented || (element.dataset.accordionExpanded === '0')) {
      return
    }
    outer.style.height = `${getOpenHeight()}px`
    outer.setAttribute('aria-expanded', 'false')
    element.dataset.changing = '1'
    selectAlso(element, '[data-class-accordion-open]').forEach((styleElement) => {
      unsetClassState(styleElement, styleElement.dataset.classAccordionOpen)
      setClassState(styleElement, styleElement.dataset.classAccordionClosing || styleElement.dataset.classAccordionClosed)
    })
    setTimeout(() => {
      outer.style.height = collapsedHeight
    }, +element.dataset.accordionDelay || 5)
    setTimeout(() => {
      element.dataset.changing = '0'
      selectAlso(element, '[data-class-accordion-closing]').forEach((styleElement) => {
        unsetClassState(styleElement, styleElement.dataset.classAccordionClosing)
        setClassState(styleElement, styleElement.dataset.classAccordionClosed)
      })
      outer.style.height = ''
    }, +element.dataset.accordionDuration || 310)
    element.dataset.accordionExpanded = '0'
  }
  const open = () => {
    let defaultPrevented = false
    const event = {
      sender: controller,
      preventDefault: () => {
        defaultPrevented = true
      },
    }
    opened.forEach(listener => listener(event))
    if (defaultPrevented || (element.dataset.accordionExpanded === '1')) {
      return
    }
    outer.setAttribute('aria-expanded', 'true')
    selectAlso(element, '[data-class-accordion-open]').forEach((styleElement) => {
      unsetClassState(styleElement, styleElement.dataset.classAccordionClosed)
      setClassState(styleElement, styleElement.dataset.classAccordionOpening || styleElement.dataset.classAccordionOpen)
    })
    element.dataset.changing = '1'
    outer.style.height = collapsedHeight
    setTimeout(() => {
      outer.style.height = `${getOpenHeight()}px`
      refreshBgImages(element)
    }, +element.dataset.accordionDelay || 5)
    setTimeout(() => {
      element.dataset.changing = '0'
      selectAlso(element, '[data-class-accordion-opening]').forEach((styleElement) => {
        unsetClassState(styleElement, styleElement.dataset.classAccordionOpening)
        setClassState(styleElement, styleElement.dataset.classAccordionOpen)
      })
      outer.style.height = 'auto'
    }, +element.dataset.accordionDuration || 310)
    element.dataset.accordionExpanded = '1'
  }
  element.dataset.changing = '0'
  button.addEventListener('click', () => {
    if (element.dataset.changing === '1') {
      return
    }
    if (element.dataset.accordionExpanded === '1') {
      close()
    } else {
      open()
    }
  })
  if (element.dataset.preExpand === '1') {
    open()
  } else {
    close()
  }
  const controller = {
    open,
    close,
    opened,
    closed,
    getOpenHeight,
  }
  return controller
})
